import React, { FunctionComponent, useMemo, useState } from 'react';

import { useUnit } from 'effector-react/ssr';

import { useId } from '@radix-ui/react-id';
import clsx from 'clsx';
import { Controller, useForm } from 'react-hook-form';

import { $profile } from '@stores/profile';

import { apiFeedbackSubmit } from '@api/methods';
import { FeedbackSubmitPayload } from '@api/protocol';

import { sendFeedbackRequestEvent } from '@utils/analytics';
import { VALIDATION_MESSAGES, VALIDATORS } from '@utils/constants';
import { handleFormError } from '@utils/form';
import { isValueSet } from '@utils/guards';
import { makeInfoPageRoute } from '@utils/route';
import { SnackBar } from '@utils/snackbar';
import { applyTypograf } from '@utils/typograf';

import { Button } from '@components/common/Button/Button';
import { InvisibleCaptcha } from '@components/common/InvisibleCaptcha/InvisibleCaptcha';
import { useInvisibleCaptcha } from '@components/common/InvisibleCaptcha/use-invisible-captcha';
import { Link } from '@components/common/Link/Link';
import { StyledCheckbox } from '@components/common/StyledCheckbox/StyledCheckbox';
import { RuPhoneInput, StyledInput, StyledTextArea } from '@components/common/StyledInput';

import styles from './FeedbackForm.module.scss';

const dataProcessingPolicyText1 = applyTypograf(
    'Я согласен на обработку персональных данных\nв соответствии с',
);
const dataProcessingPolicyText2 = applyTypograf(
    'Политикой в отношении обработки\nперсональных данных',
);
const userAgreementText1 = applyTypograf('Я принимаю условия');
const userAgreementText2 = applyTypograf('Пользовательского соглашения');

type FeedbackFormValues = {
    name: string;
    phone: string;
    message: string;
    hasAcceptedDataProcessingPolicy: boolean;
    hasAcceptedUserAgreement: boolean;
};

type FeedbackFormProps = {
    formId?: string;
    onSubmit?(): void;
    className?: string;
};
export const FeedbackForm: FunctionComponent<FeedbackFormProps> = (props) => {
    const { className, onSubmit, formId = 'feedback-form' } = props;

    const dataProcessingPolicyCheckboxId = useId();
    const userAgreementCheckboxId = useId();

    const { user } = useUnit($profile);

    const defaultValues = useMemo(
        (): FeedbackFormValues => ({
            name: user?.name ?? '',
            phone: user?.phone ?? '',
            message: '',
            hasAcceptedDataProcessingPolicy: false,
            hasAcceptedUserAgreement: false,
        }),
        [user],
    );

    const form = useForm<FeedbackFormValues>({
        defaultValues,
        shouldUnregister: true,
        mode: 'onChange',
    });

    const { control, handleSubmit, reset } = form;

    const invisibleCaptcha = useInvisibleCaptcha();
    const { getCaptchaToken, isCaptchaLoading } = invisibleCaptcha;

    const [isFormSubmitting, setIsFormSubmitting] = useState(false);

    const submit = async (formInput: FeedbackFormValues) => {
        if (isFormSubmitting) return;
        setIsFormSubmitting(true);

        let captchaToken: string | undefined;
        try {
            captchaToken = await getCaptchaToken();
        } catch {
            setIsFormSubmitting(false);
            return;
        }

        const { name, phone, message } = formInput;

        const payload: FeedbackSubmitPayload = {
            phone,
            captcha_token: captchaToken,
        };

        if (name.length > 0) {
            payload.name = name;
        }

        if (message.length > 0) {
            payload.message = message;
        }

        try {
            await apiFeedbackSubmit(payload);
            sendFeedbackRequestEvent();
            reset();
            if (isValueSet(onSubmit)) onSubmit();
        } catch (error) {
            SnackBar.handleApiError(error);
            handleFormError(form, error);
        } finally {
            setIsFormSubmitting(false);
        }
    };

    return (
        <>
            <form
                className={clsx(styles.root, className)}
                id={formId}
                onSubmit={handleSubmit(submit)}
            >
                <Controller
                    control={control}
                    name="name"
                    rules={{ validate: VALIDATORS.nameValidator }}
                    render={({ field, fieldState }) => (
                        <StyledInput
                            label="Имя"
                            className={styles.nameInput}
                            hintMessage={fieldState.error?.message}
                            hasError={fieldState.error !== undefined}
                            isKeepHintPlace
                            {...field}
                        />
                    )}
                />
                <Controller
                    control={control}
                    name="phone"
                    rules={{
                        required: VALIDATION_MESSAGES.required,
                        validate: VALIDATORS.phoneValidator,
                    }}
                    render={({ field, fieldState }) => (
                        <RuPhoneInput
                            label="Телефон*"
                            hintMessage={fieldState.error?.message}
                            hasError={fieldState.error !== undefined}
                            className={styles.phoneInput}
                            isKeepHintPlace
                            {...field}
                        />
                    )}
                />
                <Controller
                    control={control}
                    name="message"
                    rules={{ validate: VALIDATORS.messageValidator }}
                    render={({ field, fieldState }) => (
                        <StyledTextArea
                            label="Сообщение"
                            classes={{ root: styles.messageInput }}
                            hintMessage={fieldState.error?.message}
                            hasError={fieldState.error !== undefined}
                            isResizable
                            isKeepHintPlace
                            {...field}
                        />
                    )}
                />
                <Controller
                    control={control}
                    name="hasAcceptedDataProcessingPolicy"
                    rules={{
                        required: VALIDATION_MESSAGES.required,
                        validate: VALIDATORS.isChecked,
                    }}
                    render={({ field, fieldState, formState }) => (
                        <StyledCheckbox
                            id={dataProcessingPolicyCheckboxId}
                            className={styles.dataProcessingPolicyCheckbox}
                            hasError={fieldState.error !== undefined}
                            isDisabled={formState.isSubmitting}
                            {...field}
                        />
                    )}
                />
                <label
                    htmlFor={dataProcessingPolicyCheckboxId}
                    className={styles.dataProcessingPolicyCheckboxLabel}
                >
                    {dataProcessingPolicyText1}{' '}
                    <Link route={makeInfoPageRoute('privacy')} className={styles.labelLink}>
                        {dataProcessingPolicyText2}
                    </Link>
                </label>
                <Controller
                    control={control}
                    name="hasAcceptedUserAgreement"
                    rules={{
                        required: VALIDATION_MESSAGES.required,
                        validate: VALIDATORS.isChecked,
                    }}
                    render={({ field, fieldState, formState }) => (
                        <StyledCheckbox
                            id={userAgreementCheckboxId}
                            className={styles.userAgreementCheckbox}
                            hasError={fieldState.error !== undefined}
                            isDisabled={formState.isSubmitting}
                            {...field}
                        />
                    )}
                />
                <label
                    htmlFor={userAgreementCheckboxId}
                    className={styles.userAgreementCheckboxLabel}
                >
                    {userAgreementText1}{' '}
                    <Link route={makeInfoPageRoute('eula')} className={styles.labelLink}>
                        {userAgreementText2}
                    </Link>
                </label>
            </form>
            <InvisibleCaptcha {...invisibleCaptcha} />
            <Button
                type="submit"
                form={formId}
                variant="green"
                isFullWith
                isLoading={isFormSubmitting || isCaptchaLoading}
                className={styles.button}
            >
                Отправить
            </Button>
        </>
    );
};
